import React, { useState, useEffect, useCallback } from 'react';
import './BuildingChart.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import API_URL from './config';

// Регистрируем необходимые компоненты Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Словарь для перевода имен полей - вынесен на уровень компонента
const fieldTranslations = {
  'tempira': 'Прогноз погоды',
  't01': 'T01',
  't02': 'T02',
  't03': 'T03',
  't04': 'T04',
  't05': 'T05',
  't06': 'T06',
  't07': 'T07',
  't08': 'T08',
  't09': 'T09',
  't10': 'T10'
};

// Список адресов для выпадающего списка
const BUILDING_ADDRESSES = [
  { id: 98, address: "ул. Анисимова, 5, Норильск, Красноярский край" },
  { id: 120, address: "ул. Бегичева, 13, Норильск, Красноярский край" },
  { id: 249, address: "ул. Кирова, 2, Норильск, Красноярский край" },
  { id: 6, address: "Котульского проезд, 2, Норильск, Красноярский край" },
  { id: 53, address: "пр-кт Ленинский, 27 к1, Норильск, Красноярский край" },
  { id: 907, address: "пр-кт Ленинский, 27 к2, Норильск, Красноярский край" },
  { id: 27, address: "пл. Металлургов, 17, Норильск, Красноярский край" },
  { id: 946, address: "ул. Орджоникидзе, 10 с4, Норильск, Красноярский край" },
  { id: 578, address: "ул. Орджоникидзе, 7, Норильск, Красноярский край" },
  { id: 955, address: "ул. Талнахская, 10 к2, Норильск, Красноярский край" },
  { id: 771, address: "ул. Талнахская, 78, Норильск, Красноярский край" }
];

// Более яркие и контрастные цвета
const colors = [
  '#FF5722', // Оранжево-красный
  '#2196F3', // Ярко-синий
  '#4CAF50', // Зеленый
  '#9C27B0', // Фиолетовый
  '#FFC107', // Янтарный
  '#E91E63', // Розовый
  '#00BCD4', // Голубой
  '#8BC34A', // Светло-зеленый
  '#673AB7', // Темно-фиолетовый
  '#FFEB3B'  // Желтый
];

function BuildingChart({ buildingId, buildingAddress, buildingDetails, showSelector = true }) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState(buildingId || "");
  // Обновляем состояние для отслеживания видимости линий для новых полей
  const [visibleDatasets, setVisibleDatasets] = useState({
    tempira: true, // По умолчанию виден прогноз погоды
    t01: false,
    t02: false,
    t03: false,
    t04: false,
    t05: false,
    t06: false,
    t07: false,
    t08: false,
    t09: false,
    t10: false
  });

  // Эффект для синхронизации внешнего buildingId с внутренним selectedBuildingId
  useEffect(() => {
    if (buildingId) {
      setSelectedBuildingId(buildingId);
    }
  }, [buildingId]);

  // Функция для переключения видимости датасета
  const toggleDatasetVisibility = useCallback((field) => {
    setVisibleDatasets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  // Обработчик изменения выбранного здания в выпадающем списке
  const handleBuildingChange = (event) => {
    const newBuildingId = event.target.value;
    setSelectedBuildingId(newBuildingId);
  };

  // Загружаем данные при изменении selectedBuildingId
  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedBuildingId) return;
      
      setLoading(true);
      setError(null);
      setChartData(null);

      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Нет данных для этого здания");
          setLoading(false);
          return;
        }

        console.log(`Выполняется запрос прогноза для здания ID: ${selectedBuildingId}`);
        
        const endpoint = `/Neuron/building-predictions/${selectedBuildingId}`;
        console.log(`Запрос к эндпоинту: ${API_URL}${endpoint}`);
        
        const response = await fetch(`${API_URL}${endpoint}`, {
          method: "GET",
          headers: { 
            "accept": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка запроса: ${response.status}`);
        }

        const data = await response.json();
        console.log("Получены данные:", data);
        
        // Проверяем, есть ли данные
        if (!data || (Array.isArray(data) && data.length === 0)) {
          setError("Пока нет данных для этого здания");
          setLoading(false);
          return;
        }
        
        // Обработка данных из нейросети
        if (Array.isArray(data) && data.length > 0) {
          // Форматирование дат для меток оси X (день.месяц)
          const labelsMap = data.map(item => {
            const date = new Date(item.datetime);
            return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          });
          
          // Берем только уникальные даты, сохраняя порядок
          const labels = [];
          const seenDates = new Set();
          labelsMap.forEach(date => {
            if (!seenDates.has(date)) {
              seenDates.add(date);
              labels.push(date);
            }
          });
          
          const datasets = [];
          
          // Динамически создаем наборы данных для всех числовых полей, кроме datetime
          const firstItem = data[0];
          const numericFields = Object.keys(firstItem).filter(key => 
            key !== 'datetime' && typeof firstItem[key] === 'number'
          );
          
          numericFields.forEach((field, index) => {
            // Получаем перевод поля или форматируем исходное название
            const fieldLabel = fieldTranslations[field] || 
              field.replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              
            // Группируем данные по дате
            const dateData = {};
            data.forEach(item => {
              const date = new Date(item.datetime);
              const dateKey = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
              dateData[dateKey] = item[field];
            });
            
            // Формируем массив данных в порядке меток
            const dataPoints = labels.map(date => dateData[date] || null);
            
            datasets.push({
              label: fieldLabel,
              data: dataPoints,
              borderColor: colors[index % colors.length],
              backgroundColor: colors[index % colors.length] + '20',
              tension: 0.3,
              borderWidth: 3,
              pointBackgroundColor: colors[index % colors.length],
              pointBorderColor: '#fff',
              pointHoverRadius: 6,
              pointHoverBackgroundColor: colors[index % colors.length],
              pointHoverBorderColor: '#fff',
              pointHoverBorderWidth: 2,
              pointRadius: 4,
              fill: false
            });
          });
          
          setChartData({
            labels,
            datasets
          });
          setLoading(false);
        } else {
          setError("Пока нет данных для этого здания");
          setLoading(false);
        }
      } catch (err) {
        console.error("Ошибка при получении данных для графика:", err);
        setError("Не удалось загрузить данные прогноза");
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedBuildingId]);

  // Определяем цвета для графика 
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#333',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        ticks: {
          color: '#333',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    },
    plugins: {
      legend: {
        display: false, // Скрываем стандартную легенду, так как используем свою
        position: 'top',
        labels: {
          color: '#333',
          font: {
            family: 'Montserrat',
            size: 14,
            weight: 'bold'
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#333',
        bodyColor: '#333',
        titleFont: {
          family: 'Montserrat',
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: 'Montserrat',
          size: 13
        },
        padding: 12,
        cornerRadius: 6,
        displayColors: true,
        usePointStyle: true,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2);
            }
            return label;
          }
        }
      }
    }
  };

  // Если здание не выбрано, показываем соответствующее сообщение
  if (!selectedBuildingId || selectedBuildingId === "") {
    return (
      <div className="building-chart">
        {showSelector && (
        <div className="chart-header">
          <div className="building-selector">
            <select
              value={selectedBuildingId}
              onChange={handleBuildingChange}
              className="building-select"
            >
              <option value="">Выберите здание</option>
              {BUILDING_ADDRESSES.map(building => (
                <option key={building.id} value={building.id}>
                  {building.address}
                </option>
              ))}
            </select>
          </div>
        </div>
        )}
        <div className="error-container">
          <p>Выберите здание из списка для отображения прогноза</p>
        </div>
      </div>
    );
  }

  // Рендерим компонент в зависимости от состояния загрузки и данных
  const renderChart = () => {
    if (loading) {
      return (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <p>Загрузка данных прогноза...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="error-message">
          <div style={{ backgroundColor: "transparent" }}>{error}</div>
        </div>
      );
    }
    
    if (!chartData || chartData.datasets.length === 0) {
      return (
        <div className="no-data-message">
          <div>Нет данных для отображения прогноза</div>
        </div>
      );
    }
    
    // Фильтруем датасеты в соответствии с выбранными для отображения
    const filteredDatasets = chartData.datasets.filter(dataset => {
      const fieldName = Object.keys(fieldTranslations).find(key => 
        fieldTranslations[key] === dataset.label
      ) || dataset.label.toLowerCase().replace(/\s+/g, '_');
      
      return visibleDatasets[fieldName];
    });
    
    // Создаем легенду с кнопками выбора датасетов
    const renderLegend = () => (
      <div className="chart-legend-controls">
        {chartData.datasets.map((dataset, index) => {
          const fieldName = Object.keys(fieldTranslations).find(key => 
            fieldTranslations[key] === dataset.label
          ) || dataset.label.toLowerCase().replace(/\s+/g, '_');
          
          return (
            <button
              key={`legend-${index}`}
              className={`chart-legend-button ${visibleDatasets[fieldName] ? 'active' : 'inactive'}`}
              onClick={() => toggleDatasetVisibility(fieldName)}
            >
              <span 
                className="chart-legend-color" 
                style={{ backgroundColor: dataset.borderColor }}
              />
              {dataset.label}
            </button>
          );
        })}
      </div>
    );
    
    return (
      <>
        {renderLegend()}
        <div className="chart-wrapper">
          <Line 
            data={{ 
              ...chartData, 
              datasets: filteredDatasets.length > 0 ? filteredDatasets : chartData.datasets 
            }} 
            options={chartOptions} 
          />
        </div>
      </>
    );
  };

  return (
    <div className="building-chart">
      {showSelector && (
      <div className="chart-header">
        <div className="building-selector">
          <select
            value={selectedBuildingId}
            onChange={handleBuildingChange}
            className="building-select"
          >
            <option value="">Выберите здание</option>
            {BUILDING_ADDRESSES.map(building => (
              <option key={building.id} value={building.id}>
                {building.address}
              </option>
            ))}
          </select>
        </div>
      </div>
      )}
      {renderChart()}
    </div>
  );
}

export default BuildingChart;